/**
 * FogRenderer.ts
 * Draws the fog-of-war overlay in world (camera) space, sitting between the
 * terrain layer and the unit layer in the scene graph.
 *
 *   Visible    — nothing drawn (player sees the tile normally)
 *   Explored   — light veil (35 % opacity) — terrain visible, clearly out-of-sight
 *   Unexplored — fully opaque black tile
 *
 * For elevated tiles (buildings, beacon) the polygon is extended downward to
 * cover the cliff faces as well — otherwise the top of a tall building would
 * peek through the fog.
 *
 * Rebuilds the Graphics geometry only when FogSystem.dirty is true (≈ every
 * 500 ms while units are moving). Uses two-pass batching (all opaque tiles
 * first, then all explored tiles) to minimise fill-style switches.
 *
 * Performance: viewport culling skips tiles whose world-space centre lies
 * outside the current camera window + a generous margin. On a full 192×144 map
 * this cuts polygon count by ~95 % while the player is zoomed in on the centre.
 */

import { Container, Graphics } from 'pixi.js';
import { FogState } from '../types';
import type { TileMap } from '../map/TileMap';
import { ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP } from '../constants';

const HW = ISO_TILE_WIDTH  / 2; // 32 px
const HH = ISO_TILE_HEIGHT / 2; // 16 px

// Extra margin beyond the screen edge so partially-visible tiles at the border
// are never accidentally clipped. Expressed in world pixels.
const CULL_MARGIN_X = HW * 4;
const CULL_MARGIN_Y = HH * 8 + 64; // generous — tall buildings extend downward

const FOG_COLOUR = 0x080b10; // near-black with a slight blue tint

export class FogRenderer {
  private readonly g = new Graphics();
  private screenWidth  = 1280;
  private screenHeight = 720;

  /**
   * @param world   The camera-transformed world Container.
   *                FogRenderer inserts itself at index 1 (after terrain at 0,
   *                before units which are added next by UnitRenderer).
   * @param map     TileMap for tile geometry queries.
   */
  constructor(
    private readonly world: Container,
    private readonly map: TileMap,
    private readonly exploredAlpha   = 0.35,
    private readonly unexploredAlpha = 1.0,
  ) {
    const idx = Math.min(1, world.children.length);
    world.addChildAt(this.g, idx);
  }

  /** Call on resize so culling margins match the actual screen. */
  setScreenSize(w: number, h: number): void {
    this.screenWidth  = w;
    this.screenHeight = h;
  }

  /** Show or hide the entire fog overlay (used by the debug fog toggle). */
  setVisible(visible: boolean): void {
    this.g.visible = visible;
  }

  /**
   * Rebuild the fog overlay from the current fog state array.
   * Only call when FogSystem.dirty is true; clear dirty afterwards.
   *
   * Instead of iterating the full W×H grid and checking each tile's world
   * position, we invert the isometric projection to compute the col/row range
   * that overlaps the viewport, then iterate only those tiles.
   *
   * tileBase(col, row) = { x: (col - row) * HW, y: (col + row) * HH }
   * Inverse:
   *   col = (x / HW + y / HH) / 2
   *   row = (y / HH - x / HW) / 2
   *
   * This keeps rebuild cost proportional to visible tiles regardless of total
   * map size — critical when scaling to larger maps.
   */
  rebuild(fog: FogState[]): void {
    const g      = this.g;
    const width  = this.map.width;
    const height = this.map.height;

    // Viewport bounds in world space (with margins to cover partially-visible
    // tiles and tall elevated structures that extend above their base tile).
    const zoom      = this.world.scale.x || 1;
    const camLeft   = -this.world.position.x / zoom - CULL_MARGIN_X;
    const camTop    = -this.world.position.y / zoom - CULL_MARGIN_Y;
    const camRight  = camLeft + this.screenWidth  / zoom + CULL_MARGIN_X * 2;
    const camBottom = camTop  + this.screenHeight / zoom + CULL_MARGIN_Y * 2;

    // Tile range visible on screen — invert the isometric formula for all four
    // viewport corners, then take the bounding box of the resulting diamond.
    const colMin = Math.max(0,          Math.floor((camLeft  / HW + camTop    / HH) / 2) - 1);
    const colMax = Math.min(width  - 1, Math.ceil ((camRight / HW + camBottom / HH) / 2) + 1);
    const rowMin = Math.max(0,          Math.floor((camTop   / HH - camRight  / HW) / 2) - 1);
    const rowMax = Math.min(height - 1, Math.ceil ((camBottom / HH - camLeft  / HW) / 2) + 1);

    g.clear();

    // Pass 1 — fully opaque unexplored tiles
    g.beginFill(FOG_COLOUR, this.unexploredAlpha);
    for (let row = rowMin; row <= rowMax; row++) {
      for (let col = colMin; col <= colMax; col++) {
        if (fog[row * width + col] === FogState.Unexplored) this.drawTilePoly(col, row);
      }
    }
    g.endFill();

    // Pass 2 — semi-transparent explored tiles
    g.beginFill(FOG_COLOUR, this.exploredAlpha);
    for (let row = rowMin; row <= rowMax; row++) {
      for (let col = colMin; col <= colMax; col++) {
        if (fog[row * width + col] === FogState.Explored) this.drawTilePoly(col, row);
      }
    }
    g.endFill();
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private drawTilePoly(col: number, row: number): void {
    const c  = this.map.tileTopCenter(col, row);
    const fh = this.map.elevation(col, row) * ELEVATION_STEP;

    if (fh > 0) {
      this.g.drawPolygon([
        c.x,       c.y - HH,
        c.x + HW,  c.y,
        c.x + HW,  c.y + fh,
        c.x,       c.y + HH + fh,
        c.x - HW,  c.y + fh,
        c.x - HW,  c.y,
      ]);
    } else {
      this.g.drawPolygon([
        c.x,       c.y - HH,
        c.x + HW,  c.y,
        c.x,       c.y + HH,
        c.x - HW,  c.y,
      ]);
    }
  }
}
